import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { useEvent } from "../hooks/useEvent";
import { ANALYTICS_EVENTS } from "../Constants/analyticsEvents";
import Reveal from "./Reveal";
import SectionTitle from "./SectionTitle";
import Magnetic from "./Magnetic";

const CONTACT_EMAIL = "ozhayun2@gmail.com";

export default function ContactMe() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const track = useEvent();

  const validate = () => {
    const errors = {};
    if (!formData.name) errors.name = "Name is required";
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }
    if (!formData.message) errors.message = "Message is required";
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    setStatus("sending");
    emailjs
      .send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        {
          name: formData.name, // Maps to ${name} in the template
          email: formData.email, // Maps to ${email} in the template
          message: formData.message, // Maps to ${message} in the template
        },
        process.env.REACT_APP_PUBLIC_KEY
      )
      .then(() => {
        track(ANALYTICS_EVENTS.EMAIL_SENT);
        setStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setErrors({});
      })
      .catch((error) => {
        console.error("FAILED...", error);
        setStatus("error");
      });
  };

  const sending = status === "sending";

  return (
    <section id="contact-me" className="py-10 pb-24">
      <div className="flex flex-col items-center justify-center">
        <SectionTitle>Contact Me</SectionTitle>
        <Reveal delay={0.15}>
        <form
          onSubmit={handleSubmit}
          className="glass-card w-[min(36rem,92vw)] rounded-2xl border border-biolum-green/30 p-8 shadow-[0_0_40px_rgba(100,255,218,0.12)] justify-center items-center flex flex-wrap selection:bg-biolum-green"
        >
          <p className="mb-6 w-full text-center text-lg text-slate-200">
            Have an idea, a role, or just want to talk AI?{" "}
            <span className="text-biolum-green">Let&apos;s connect.</span>
          </p>
          <div className="w-full mb-4">
            <label htmlFor="contact-name" className="mb-1 block text-sm text-slate-400">
              Name
            </label>
            <input
              id="contact-name"
              type="text"
              name="name"
              autoComplete="name"
              value={formData.name}
              onChange={handleChange}
              className="block w-full p-3 mt-1 text-slate-100 placeholder-slate-400 bg-ocean-blue/60 backdrop-blur-md border border-biolum-green/40 rounded-md focus:border-biolum-green focus:ring-1 focus:ring-biolum-green outline-none transition-all"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name}</p>
            )}
          </div>
          <div className="w-full mb-4">
            <label htmlFor="contact-email" className="mb-1 block text-sm text-slate-400">
              Email
            </label>
            <input
              id="contact-email"
              type="email"
              name="email"
              autoComplete="email"
              spellCheck={false}
              value={formData.email}
              onChange={handleChange}
              className="block w-full p-3 mt-1 text-slate-100 placeholder-slate-400 bg-ocean-blue/60 backdrop-blur-md border border-biolum-green/40 rounded-md focus:border-biolum-green focus:ring-1 focus:ring-biolum-green outline-none transition-all"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>
          <div className="w-full mb-4">
            <label htmlFor="contact-message" className="mb-1 block text-sm text-slate-400">
              Message
            </label>
            <textarea
              id="contact-message"
              name="message"
              autoComplete="off"
              value={formData.message}
              onChange={handleChange}
              className="block w-full p-3 mt-1 text-slate-100 placeholder-slate-400 bg-ocean-blue/60 backdrop-blur-md border border-biolum-green/40 rounded-md focus:border-biolum-green focus:ring-1 focus:ring-biolum-green outline-none transition-all"
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-400">{errors.message}</p>
            )}
          </div>
          <Magnetic>
            <button
              type="submit"
              disabled={sending}
              className="hover:bg-biolum-blue inline-block px-12 py-3 text-lg transition-colors bg-biolum-green text-ocean-dark font-bold rounded-lg shadow-[0_0_25px_rgba(100,255,218,0.5)] ripple-effect disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
          </Magnetic>
          <p aria-live="polite" className="mt-4 w-full text-center text-sm">
            {status === "success" && (
              <span className="text-biolum-green">
                Message sent, thanks! I&apos;ll get back to you soon.
              </span>
            )}
            {status === "error" && (
              <span className="text-red-400">
                Something went wrong. Email me directly at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </span>
            )}
          </p>
          <p className="mt-2 w-full text-center text-xs text-slate-500">
            Or reach me directly at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-biolum-green hover:underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </form>
        </Reveal>
      </div>
    </section>
  );
}
